import { type SxProps, type Theme } from '@mui/material/styles';
import MuiTypography, { type TypographyProps as MuiTypographyProps } from '@mui/material/Typography';
import React, { type CSSProperties, forwardRef } from 'react';

export type TypographyProps<C extends React.ElementType = 'span'> = Omit<
  MuiTypographyProps<
    C,
    {
      component?: C;
    }
  >,
  'component' | 'color'
> & {
  ellipsis?: boolean;
  component?: C;
  color?: 'default' | 'primary' | 'secondary' | 'white' | 'error' | 'success' | 'warning' | 'inherit';
  fontWeight?: CSSProperties['fontWeight'];
};

const getEllipsisStyles = (ellipsis: boolean) => {
  if (!ellipsis) return {};

  return {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
};

export const getColor = (color: TypographyProps['color'], palette: Theme['palette']) => {
  switch (color) {
    case 'default':
      return palette.neutral[100];
    case 'primary':
      return palette.primary.main;
    case 'secondary':
      return palette.neutral[60];
    case 'white':
      return palette.layout[100];
    case 'error':
      return palette.error[200];
    case 'success':
      return palette.success[200];
    case 'warning':
      return palette.warning[300];
    case 'inherit':
      return 'inherit';
    default:
      return undefined;
  }
};

const Component = <C extends React.ElementType = 'span'>(
  { ellipsis = false, sx, component, color, fontWeight, ...props }: TypographyProps<C>,
  ref: React.ForwardedRef<HTMLElement>
) => {
  const baseSx: SxProps<Theme> = (theme) => ({
    color: getColor(color, theme.palette),
    fontWeight,
    ...getEllipsisStyles(ellipsis),
  });

  return (
    <MuiTypography
      ref={ref}
      component={component}
      sx={[baseSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      {...props}
    />
  );
};

export const Typography = forwardRef(Component) as <C extends React.ElementType = 'span'>(
  props: TypographyProps<C>
) => React.ReactElement;
